import { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import { useTranslation } from 'react-i18next';
import { LESSONS } from './VigilAcademyData';
import './VigilAcademy.css';

const STORAGE_KEY = 'vigil-academy-progress';

const XP_LEVELS = [
  { min: 0,    max: 499,  title: 'Novice Scout' },
  { min: 500,  max: 999,  title: 'Rule Writer' },
  { min: 1000, max: 1999, title: 'Security Analyst' },
  { min: 2000, max: Infinity, title: 'Vigil Master' },
];

function getLevelTitle(xp) {
  return (XP_LEVELS.find(l => xp >= l.min && xp <= l.max) || XP_LEVELS[0]).title;
}

function getXpPct(xp) {
  const level = XP_LEVELS.find(l => xp >= l.min && xp <= l.max) || XP_LEVELS[0];
  if (level.max === Infinity) return 100;
  const range = level.max - level.min + 1;
  return Math.min(100, Math.round(((xp - level.min) / range) * 100));
}

/* ─── Theory renderer ─────────────────────────────────────────────────── */
function renderTheory(text) {
  const lines = text.split('\n');
  const elements = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Triple-backtick code block
    if (line.startsWith('```')) {
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      elements.push(
        <pre key={key++} className="va-code-block">
          {codeLines.join('\n')}
        </pre>
      );
      i++; // skip closing ```
      continue;
    }

    // Heading 2
    if (line.startsWith('## ')) {
      elements.push(<h2 key={key++}>{line.slice(3)}</h2>);
      i++;
      continue;
    }

    // Heading 3
    if (line.startsWith('### ')) {
      elements.push(<h3 key={key++}>{line.slice(4)}</h3>);
      i++;
      continue;
    }

    // Table
    if (line.startsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      const rows = tableLines.filter(r => !r.match(/^\|[-| ]+\|$/));
      if (rows.length > 0) {
        const headerCells = rows[0].split('|').filter((_, idx, arr) => idx > 0 && idx < arr.length - 1);
        const bodyRows = rows.slice(1);
        elements.push(
          <table key={key++}>
            <thead>
              <tr>{headerCells.map((c, ci) => <th key={ci}>{inlineCode(c.trim())}</th>)}</tr>
            </thead>
            <tbody>
              {bodyRows.map((row, ri) => {
                const cells = row.split('|').filter((_, idx, arr) => idx > 0 && idx < arr.length - 1);
                return (
                  <tr key={ri}>
                    {cells.map((c, ci) => <td key={ci}>{inlineCode(c.trim())}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        );
      }
      continue;
    }

    // Empty line — paragraph break
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Normal paragraph
    elements.push(<p key={key++}>{inlineCode(line)}</p>);
    i++;
  }

  return elements;
}

function inlineCode(text) {
  const parts = text.split(/(`[^`]+`)/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) => {
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return <code key={i} className="va-code-inline">{part.slice(1, -1)}</code>;
    }
    return part;
  });
}

/* ─── AcademySidebar ──────────────────────────────────────────────────── */
function AcademySidebar({ lessons, progress, currentId, onSelect }) {
  const levelOrder = ['novice', 'beginner', 'intermediate', 'advanced', 'expert'];
  const grouped = {};
  lessons.forEach(l => {
    if (!grouped[l.level]) grouped[l.level] = [];
    grouped[l.level].push(l);
  });

  const levelMeta = {};
  lessons.forEach(l => {
    if (!levelMeta[l.level]) {
      levelMeta[l.level] = { label: l.levelLabel, emoji: l.levelEmoji, totalXp: 0, earnedXp: 0 };
    }
    levelMeta[l.level].totalXp += l.xp;
    if (progress[l.id]?.completed) {
      levelMeta[l.level].earnedXp += l.xp;
    }
  });

  const levels = levelOrder.filter(lv => grouped[lv]);

  return (
    <div className="va-sidebar">
      {levels.map(level => (
        <div key={level} className="va-level-section">
          <div className="va-level-header">
            <span>{levelMeta[level].emoji} {levelMeta[level].label}</span>
            <span className="va-level-xp-total">{levelMeta[level].earnedXp}/{levelMeta[level].totalXp} XP</span>
          </div>
          {(grouped[level] || []).map(lesson => {
            const done = !!progress[lesson.id]?.completed;
            const active = lesson.id === currentId;
            return (
              <div
                key={lesson.id}
                className={`va-lesson-item${active ? ' active' : ''}${done ? ' completed' : ''}`}
                onClick={() => onSelect(lesson.id)}
              >
                <span className="va-lesson-status">
                  {done ? '✅' : active ? '▶' : '○'}
                </span>
                <span className="va-lesson-title">{lesson.title}</span>
                <span className="va-lesson-xp">{lesson.xp} XP</span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* ─── TheoryPanel ─────────────────────────────────────────────────────── */
function TheoryPanel({ lesson }) {
  if (!lesson) return null;
  return (
    <div className="va-theory">
      {renderTheory(lesson.theory)}
    </div>
  );
}

/* ─── ExercisePanel ───────────────────────────────────────────────────── */
function ExercisePanel({
  lesson,
  userRule,
  onRuleChange,
  onCheck,
  onHint,
  onSolution,
  hintsShown,
  showSolution,
  checking,
}) {
  const { t } = useTranslation();
  if (!lesson) return null;

  return (
    <div className="va-exercise">
      <div className="va-lesson-header">
        <div className="va-lesson-meta">
          <span className={`va-level-badge ${lesson.level}`}>
            {lesson.levelEmoji} {lesson.levelLabel}
          </span>
          <span className="va-module-label">Module {lesson.module}: {lesson.moduleTitle}</span>
          <span className="va-xp-badge">+{lesson.xp} XP</span>
        </div>
        <h1 className="va-lesson-title-main">{lesson.title}</h1>
      </div>

      <div className="va-task-box">
        <div className="va-task-label">🎯 Your Task</div>
        {lesson.task}
      </div>

      <div className="va-snippet-label">
        <span>Code Snippet</span>
        <span className="va-lang-badge">{lesson.language}</span>
      </div>
      <div className="va-snippet-box">{lesson.snippet}</div>

      <div className="va-editor-section">
        <div className="va-editor-label">Your Vigil Rule</div>
        <textarea
          className="va-rule-editor"
          value={userRule}
          onChange={e => onRuleChange(e.target.value)}
          spellCheck={false}
          autoComplete="off"
        />
      </div>

      <div className="va-controls">
        <button
          className="va-btn va-btn-run"
          onClick={onCheck}
          disabled={checking || !userRule.trim()}
        >
          {checking ? <span className="va-spinner" /> : null}
          {checking ? 'Running…' : t('vigilAcademy.run', '▶ Run Test')}
        </button>
        <button
          className="va-btn va-btn-hint"
          onClick={onHint}
          disabled={hintsShown >= lesson.hints.length}
        >
          {t('vigilAcademy.hint', '💡 Hint')}
        </button>
        <button
          className="va-btn va-btn-solution"
          onClick={onSolution}
          disabled={showSolution}
        >
          {t('vigilAcademy.solution', '👁 Solution')}
        </button>
        {hintsShown > 0 && (
          <span className="va-hints-count">{hintsShown}/{lesson.hints.length} hints used</span>
        )}
      </div>

      {hintsShown > 0 && (
        <div className="va-hint-box">
          <div className="va-hint-label">Hints ({hintsShown} shown)</div>
          <ul className="va-hint-list">
            {lesson.hints.slice(0, hintsShown).map((h, i) => (
              <li key={i}>{inlineCode(h)}</li>
            ))}
          </ul>
        </div>
      )}

      {showSolution && (
        <div className="va-solution-box">
          <div className="va-solution-label">Solution</div>
          <pre className="va-solution-code">{lesson.solution}</pre>
        </div>
      )}
    </div>
  );
}

/* ─── ResultPanel ─────────────────────────────────────────────────────── */
function ResultPanel({ result, lesson, onNext, hasNext }) {
  const { t } = useTranslation();
  if (!result) return null;

  const findings = Array.isArray(result.findings) ? result.findings : [];

  return (
    <div className="va-results">
      <div className={`va-result-banner${result.passed ? ' passed va-celebrate' : ' failed'}`}>
        <div className="va-result-banner-left">
          <span>{result.passed ? t('vigilAcademy.passed', '✅ Test Passed!') : t('vigilAcademy.failed', '❌ Test Failed')}</span>
          {result.passed && result.xpEarned > 0 && (
            <span className="va-xp-earned">+{result.xpEarned} {t('vigilAcademy.xpEarned', 'XP Earned')}</span>
          )}
        </div>
        {result.passed && hasNext && (
          <button className="va-btn-next" onClick={onNext}>
            {t('vigilAcademy.next', 'Next Lesson →')}
          </button>
        )}
      </div>

      {result.message && (
        <div className="va-result-message">{result.message}</div>
      )}

      {findings.length > 0 && (
        <>
          <div className="va-findings-label">{findings.length} Finding{findings.length !== 1 ? 's' : ''}</div>
          {findings.map((f, i) => (
            <div key={i} className="va-finding-card">
              <div className="va-finding-header">
                <span className={`va-sev-badge ${f.severity || (lesson && lesson.severity) || 'info'}`}>
                  {(f.severity || (lesson && lesson.severity) || 'info').toUpperCase()}
                </span>
                {f.line != null && (
                  <span className="va-finding-line">Line {f.line}</span>
                )}
                {f.title && <span style={{ color: '#e2e8f0', fontSize: 13 }}>{f.title}</span>}
              </div>
              {(f.snippet || f.code || f.text) && (
                <div className="va-finding-snippet">{f.snippet || f.code || f.text}</div>
              )}
              {f.message && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#8b949e' }}>{f.message}</div>
              )}
            </div>
          ))}
        </>
      )}

      {result.passed && !hasNext && (
        <div className="va-completed-all">
          🎓 You've completed all lessons! You are a Vigil Master.
        </div>
      )}
    </div>
  );
}

/* ─── Main Component ──────────────────────────────────────────────────── */
export default function VigilAcademy() {
  const { token } = useAuth();
  const { t } = useTranslation();

  const [progress, setProgress] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    } catch {
      return {};
    }
  });

  const [currentLessonId, setCurrentLessonId] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      const first = LESSONS.find(l => !saved[l.id]?.completed);
      return first ? first.id : LESSONS[0].id;
    } catch {
      return LESSONS[0].id;
    }
  });

  const [lessonRules, setLessonRules] = useState({});
  const [checkResult, setCheckResult] = useState(null);
  const [hintsShown, setHintsShown] = useState(0);
  const [showSolution, setShowSolution] = useState(false);
  const [checking, setChecking] = useState(false);
  const [activeTab, setActiveTab] = useState('exercise');

  const currentLesson = useMemo(
    () => LESSONS.find(l => l.id === currentLessonId) || LESSONS[0],
    [currentLessonId]
  );

  const userRule = lessonRules[currentLessonId] !== undefined
    ? lessonRules[currentLessonId]
    : currentLesson.starterRule;

  const setUserRule = useCallback((val) => {
    setLessonRules(prev => ({ ...prev, [currentLessonId]: val }));
  }, [currentLessonId]);

  const totalXp = useMemo(
    () => LESSONS.reduce((sum, l) => sum + (progress[l.id]?.completed ? l.xp : 0), 0),
    [progress]
  );

  const levelTitle = getLevelTitle(totalXp);
  const xpPct = getXpPct(totalXp);

  const nextLesson = useMemo(() => {
    const idx = LESSONS.findIndex(l => l.id === currentLessonId);
    return LESSONS.slice(idx + 1).find(l => !progress[l.id]?.completed) || null;
  }, [currentLessonId, progress]);

  const handleLessonSelect = useCallback((id) => {
    setCurrentLessonId(id);
    setCheckResult(null);
    setHintsShown(0);
    setShowSolution(false);
    setActiveTab('exercise');
  }, []);

  const handleCheck = useCallback(async () => {
    if (!currentLesson || checking) return;
    setChecking(true);
    setCheckResult(null);

    try {
      const response = await axios.post(
        `${config.BASE_URL}/vigil/academy/check`,
        {
          rule: userRule,
          snippet: currentLesson.snippet,
          language: currentLesson.language,
          assertions: currentLesson.assertions,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = response.data;
      const passed = !!data.passed;
      let xpEarned = 0;

      if (passed && !progress[currentLesson.id]?.completed) {
        xpEarned = currentLesson.xp;
        const newProgress = {
          ...progress,
          [currentLesson.id]: {
            completed: true,
            xp: currentLesson.xp,
            hintsUsed: hintsShown,
          },
        };
        setProgress(newProgress);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newProgress));
      }

      setCheckResult({ ...data, passed, xpEarned });
    } catch (err) {
      const status = err.response?.status;
      const apiMsg = err.response?.data?.error || err.response?.data?.message;

      if (status === 404 || status === 500 || !err.response) {
        setCheckResult({
          passed: false,
          findings: [],
          message: apiMsg || 'Could not reach the backend. Try solving the exercise locally — check your solution against the lesson hints.',
          xpEarned: 0,
        });
      } else {
        setCheckResult({
          passed: false,
          findings: [],
          message: apiMsg || `Error: ${err.message}`,
          xpEarned: 0,
        });
      }
    } finally {
      setChecking(false);
    }
  }, [currentLesson, userRule, token, progress, hintsShown, checking]);

  const handleHint = useCallback(() => {
    if (!currentLesson) return;
    setHintsShown(prev => Math.min(prev + 1, currentLesson.hints.length));
  }, [currentLesson]);

  const handleSolution = useCallback(() => {
    if (!currentLesson) return;
    setShowSolution(true);
    setUserRule(currentLesson.solution);
  }, [currentLesson, setUserRule]);

  const handleNext = useCallback(() => {
    if (!nextLesson) return;
    handleLessonSelect(nextLesson.id);
  }, [nextLesson, handleLessonSelect]);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  return (
    <div className="va-page">
      <div className="va-xp-header">
        <span>🎓 {t('vigilAcademy.title', 'Vigil Academy')}</span>
        <div className="va-xp-bar-container">
          <div className="va-xp-bar-fill" style={{ width: `${xpPct}%` }} />
        </div>
        <span>{totalXp} XP — {levelTitle}</span>
      </div>

      <div className="va-layout">
        <AcademySidebar
          lessons={LESSONS}
          progress={progress}
          currentId={currentLessonId}
          onSelect={handleLessonSelect}
        />

        <div className="va-main">
          <div className="va-tabs">
            <button
              className={activeTab === 'theory' ? 'va-tab active' : 'va-tab'}
              onClick={() => handleTabChange('theory')}
            >
              {t('vigilAcademy.theory', '📖 Theory')}
            </button>
            <button
              className={activeTab === 'exercise' ? 'va-tab active' : 'va-tab'}
              onClick={() => handleTabChange('exercise')}
            >
              {t('vigilAcademy.exercise', '✏️ Exercise')}
            </button>
          </div>

          <div className="va-content">
            {activeTab === 'theory' ? (
              <TheoryPanel lesson={currentLesson} />
            ) : (
              <ExercisePanel
                lesson={currentLesson}
                userRule={userRule}
                onRuleChange={setUserRule}
                onCheck={handleCheck}
                onHint={handleHint}
                onSolution={handleSolution}
                hintsShown={hintsShown}
                showSolution={showSolution}
                checking={checking}
              />
            )}
          </div>

          {checkResult && (
            <ResultPanel
              result={checkResult}
              lesson={currentLesson}
              onNext={handleNext}
              hasNext={!!nextLesson}
            />
          )}
        </div>
      </div>
    </div>
  );
}
